/**
 * 单个文件行组件
 *
 * 显示文件名、上传进度、状态标签和操作按钮。
 * 可通过 dnd-kit 拖拽排序/合并。
 */
import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Progress, Tag, Button, Tooltip, Space, Typography } from "antd";
import {
    FileOutlined,
    FilePdfOutlined,
    FileExcelOutlined,
    FileWordOutlined,
    FileImageOutlined,
    DeleteOutlined,
    ScissorOutlined,
    WarningOutlined,
    CloseCircleOutlined,
    CheckCircleOutlined,
    LoadingOutlined,
    DragOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const FILE_ICONS = {
    "application/pdf": <FilePdfOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": <FileExcelOutlined style={{ color: "#52c41a", fontSize: 18 }} />,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": <FileWordOutlined style={{ color: "#1890ff", fontSize: 18 }} />,
    "image/jpeg": <FileImageOutlined style={{ color: "#faad14", fontSize: 18 }} />,
    "image/png": <FileImageOutlined style={{ color: "#faad14", fontSize: 18 }} />,
};

const PHASE_CONFIG = {
    CHECKING: { label: "检查中", color: "processing", icon: <LoadingOutlined spin /> },
    CHECK_FAILED: { label: "不可上传", color: "error", icon: <CloseCircleOutlined /> },
    NEEDS_SPLIT: { label: "需拆分", color: "warning", icon: <ScissorOutlined /> },
    UPLOADING: { label: "上传中", color: "processing", icon: <LoadingOutlined spin /> },
    UPLOADED: { label: "就绪", color: "success", icon: <CheckCircleOutlined /> },
    UPLOAD_FAILED: { label: "上传失败", color: "error", icon: <CloseCircleOutlined /> },
};

const FileItem = ({ file, onRemove, onSplit, onRemoveFromGroup, isGroupMember = false, isDragOverlay = false }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: file.id, disabled: isDragOverlay });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };

    const icon = FILE_ICONS[file.contentType] || <FileOutlined style={{ fontSize: 18 }} />;
    const config = PHASE_CONFIG[file.phase] || {};
    const isPdf = file.contentType === "application/pdf";
    const canSplit = isPdf && (file.phase === "UPLOADED" || file.phase === "NEEDS_SPLIT");
    const isNeedsSplit = file.phase === "NEEDS_SPLIT";
    const fileSizeMB = file.fileSize ? (file.fileSize / 1024 / 1024).toFixed(1) : null;

    const metaItems = [];
    if (file.pageCount) metaItems.push(`${file.pageCount}页`);
    if (file.charCount) metaItems.push(`${file.charCount.toLocaleString()}字符`);
    if (fileSizeMB) metaItems.push(`${fileSizeMB}MB`);
    if (file.isSplit && file.splitPages) metaItems.push(`页${file.splitPages}`);

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`upload-file-item ${isDragging ? "upload-file-item--dragging" : ""} ${file.phase === "CHECK_FAILED" ? "upload-file-item--error" : ""} ${isNeedsSplit ? "upload-file-item--needs-split" : ""}`}
        >
            {/* 拖拽热区：覆盖图标+文件名+元数据的整个左侧区域 */}
            <div className="upload-file-item__drag-zone" {...attributes} {...listeners}>
                <div className="upload-file-item__icon">{icon}</div>
                <div className="upload-file-item__info">
                    <div className="upload-file-item__name">
                        <Tooltip title={file.name}>
                            <Text ellipsis className="upload-file-item__name-text">{file.name}</Text>
                        </Tooltip>
                        {file.isSplit && (
                            <Tag color="green" style={{ marginLeft: 4, fontSize: 10 }}>拆分</Tag>
                        )}
                    </div>
                    {metaItems.length > 0 && (
                        <Text type="secondary" style={{ fontSize: 11 }}>{metaItems.join(" · ")}</Text>
                    )}
                    {file.issues.length > 0 && (
                        <div style={{ marginTop: 2 }}>
                            {file.issues.map((issue, i) => (
                                <Text
                                    key={i}
                                    type={issue.severity === "error" ? "danger" : "warning"}
                                    style={{ fontSize: 11, display: "block" }}
                                >
                                    {issue.severity === "error" ? "❌ " : "⚠ "}{issue.message}
                                </Text>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* 上传进度 */}
            <div className="upload-file-item__progress">
                {file.phase === "UPLOADING" && (
                    <Progress
                        percent={file.uploadPercent}
                        size="small"
                        showInfo={false}
                        style={{ width: 80 }}
                        status="active"
                    />
                )}
                {file.phase !== "UPLOADING" && (
                    <Tag color={config.color} icon={config.icon} style={{ margin: 0 }}>
                        {config.label}
                    </Tag>
                )}
            </div>

            {/* 操作 */}
            <div className="upload-file-item__actions">
                <Space size={4}>
                    {canSplit && (
                        <Tooltip title={isNeedsSplit ? "此文件页数较多，建议拆分" : "拆分文件"}>
                            <Button
                                type={isNeedsSplit ? "primary" : "text"}
                                size="small"
                                icon={<ScissorOutlined />}
                                onClick={() => onSplit?.(file)}
                                className={isNeedsSplit ? "upload-file-item__split-btn--highlight" : ""}
                            />
                        </Tooltip>
                    )}
                    {isGroupMember && (
                        <Tooltip title="移出组">
                            <Button type="text" size="small" onClick={() => onRemoveFromGroup?.(file.id, file.groupId)}>
                                移出
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip title="删除">
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onRemove?.(file.id)} />
                    </Tooltip>
                </Space>
            </div>
        </div>
    );
};

export default FileItem;
